/**
 * A PNG of the page as it is on screen: the top bar and page, not the sidebar, the Tasks panel,
 * dialogs or toasts. The page is drawn from its HTML (no screen-capture permission), so images
 * from other sites that don't allow it (some Shopify images) come out blank.
 */

import { prefCodec, readPref, UI_PREFS, withCodec, writePref } from '@/lib/ui-prefs';

const SKIP = '[aria-label="Tasks panel"], [data-sonner-toaster]';

const ATTACH_SCREENSHOT = withCodec(UI_PREFS.tasksAttachScreenshot, prefCodec.flag(false));

/** "Attach a screenshot of this page" is remembered per browser (off until turned on). */
export function readAttachScreenshot(): boolean {
  return readPref(ATTACH_SCREENSHOT);
}

export function saveAttachScreenshot(on: boolean): void {
  writePref(ATTACH_SCREENSHOT, on);
}

function stamp(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}${pad(date.getMonth() + 1)}${pad(date.getDate())}-${pad(date.getHours())}${pad(date.getMinutes())}${pad(date.getSeconds())}`;
}

export async function capturePage(): Promise<File | null> {
  const page = document.querySelector<HTMLElement>('[data-slot="sidebar-inset"]');
  if (!page) return null;
  // Loaded only when a screenshot is wanted.
  const { domToCanvas } = await import('modern-screenshot');

  const rect = page.getBoundingClientRect();
  const top = Math.max(0, -rect.top); // how far the page is scrolled past its top
  const height = Math.min(window.innerHeight, rect.bottom) - Math.max(0, rect.top);
  if (height <= 0) return null;
  const scale = Math.min(2, window.devicePixelRatio || 1);

  // Only down to the bottom of the screen, then cropped to what's in view.
  const full = await domToCanvas(page, {
    scale,
    height: top + height,
    style: { overflow: 'hidden' },
    backgroundColor: getComputedStyle(document.body).backgroundColor || '#ffffff',
    filter: (node) => !(node instanceof Element && node.matches(SKIP)),
  });
  const out = document.createElement('canvas');
  out.width = Math.round(rect.width * scale);
  out.height = Math.round(height * scale);
  out.getContext('2d')?.drawImage(full, 0, Math.round(top * scale), out.width, out.height, 0, 0, out.width, out.height);

  const blob = await new Promise<Blob | null>((resolve) => out.toBlob(resolve, 'image/png'));
  return blob ? new File([blob], `page-${stamp(new Date())}.png`, { type: 'image/png' }) : null;
}
